import { useEffect, type ComponentType } from "react";
import { Icon } from "@v0y4ge/jrni-ui";
import { ROUTE_IDS, type RouteId } from "./routeTable";
import { Screen } from "./chrome";
import { useNav } from "./router";
import EventsList from "../screens/events/EventsList";
/* LOCAL PATCH (platform side, 2026-09-11 customers + automatic-emails cut): THREE
   Gold Master screen registrations below are HELD BACK — not reversed — because the
   modules they import are not part of this cut: their api-client operations are not
   on `main` yet (`getStaffAvailability` for the person page, the booking-settings
   family for Booking Settings).
     - `"messaging.appointments"` keeps `ApptComms`, the served booking-communications
       screen, instead of `BookingSettings`; this continues the 2026-09-09
       "Platform-ready release cut" holdback and does not reverse the replacement.
     - `"people.person": PersonPage` and `roles: RolesScreen` are dropped: both are
       exports of the Gold Master `screens/people/StaffAndRoles.tsx`, which this cut
       does not take, so `main`'s StaffAndRoles (default export only) stays. REAL_SCREENS
       is a Partial<Record<RouteId, …>>, so the `people.person` and `roles` route ids
       that routeTable.ts registers resolve to the honest "not yet ported" stub until
       those modules land.
   Everything else in this file is byte-identical to Gold Master. */
import StaffAndRoles from "../screens/people/StaffAndRoles";
import LocationsList from "../screens/locations/LocationsList";
import LocationEditorPage from "../screens/locations/LocationEditorPage";
import LocationsImport from "../screens/locations/LocationsImport";
import NodeTreeScreen from "../screens/nodes/NodeTreeScreen";
import AttributesScreen from "../screens/nodes/AttributesScreen";
import EventTemplates from "../screens/templates/EventTemplates";
import TemplateDetail from "../screens/templates/TemplateDetail";
import PromosLibrary from "../screens/promos/PromosLibrary";
import PromoEditorPage from "../screens/promos/PromoEditorPage";
import TicketTypesLibrary from "../screens/tickets/TicketTypesLibrary";
import TicketTypeEditorPage from "../screens/tickets/TicketTypeEditorPage";
import TicketGroupsLibrary from "../screens/tickets/TicketGroupsLibrary";
import TicketGroupEditorPage from "../screens/tickets/TicketGroupEditorPage";
import Dashboard from "../screens/dashboard/Dashboard";
import NeedsAttention from "../screens/dashboard/NeedsAttention";
import EventsArchived from "../screens/events/EventsArchived";
import LandingPagesIndex from "../screens/landing/LandingPagesIndex";
import LandingPageEditor from "../screens/landing/LandingBuilder";
import HubPagesIndex from "../screens/hubs/HubPagesIndex";
import HubEditor from "../screens/hubs/HubEditor";
import { HubCreateBasics, HubCreatePage, HubCreatePublish, HubCreateLive } from "../screens/hubs/HubWizard";
import { PublicLocationHub, PublicGroupHub, PublicWorkspaceHub, PublicEventLanding, PublicEventRegister } from "../screens/hubs/PublicHubs";
import InboxView from "../screens/inbox/InboxView";
import QuestionsLibrary, { QuestionGroupsLibrary } from "../screens/questions/QuestionsLibrary";
import QuestionEditorPage from "../screens/questions/QuestionEditorPage";
import QuestionGroupEditorPage from "../screens/questions/QuestionGroupEditorPage";
import FlowBuilderPage from "../screens/builder/FlowBuilderPage";
import MessagingLibrary from "../screens/messaging/MessagingLibrary";
import MessageComposer from "../screens/messaging/MessageComposer";
// Email WYSIWYG designer (2026-07-30, Greg — messaging overhaul step 2).
// Additive: the composer route is unchanged. See screens/messaging/email/.
import EmailDesigner from "../screens/messaging/email/EmailDesigner";
import FlowsLibrary from "../screens/messaging/FlowsLibrary";
import DeliveryLog from "../screens/messaging/DeliveryLog";
// Booking Settings (2026-09-08, Nick) — REPLACES Booking comms (ApptComms) outright
// at the same route (operator). ApptComms.tsx is quarantined:
// quarantine/2026-0908-apptcomms-booking-comms/WHY.md.
import ApptComms from "../screens/messaging/ApptComms";
// Automatic emails (2026-09-11, studio-messaging-v1) — the automatic customer
// emails the platform sends, grouped Appointments / Events / Queues, one switch
// per row. Declares rail="messaging" like its siblings, so Messaging stays the
// single highlighted rail item (rail HARD RULE).
import AutomaticEmails, { MessagingSettingsScreen } from "../screens/messaging/AutomaticEmails";
import ReportsAppointments from "../screens/reports/ReportsAppointments";
import SurveysPage, { SurveyQuestionsPage, SurveyEditorPage } from "../screens/surveys/SurveyEditorPage";
import CampaignsList from "../screens/campaigns/CampaignsList";
import ApprovalsInbox, { ApproverReviewScreen } from "../screens/approvals/ApprovalsInbox";
import ReportsOverview from "../screens/reports/ReportsOverview";
import ReportsCampusPulse from "../screens/reports/ReportsCampusPulse";
import { EventReportsLibrary, EventReportDetail } from "../screens/reports/EventReports";
import CalendarWeek from "../screens/calendar/CalendarWeek";
import CalendarMonth from "../screens/calendar/CalendarMonth";
import CalendarDay from "../screens/calendar/CalendarDay";
import AppointmentsBook from "../screens/appointments/AppointmentsBook";
import AppointmentDetail from "../screens/appointments/AppointmentDetail";
import ManageBooking from "../screens/appointments/ManageBooking";
import SettingsBranding from "../screens/settings/SettingsBranding";
import SettingsClosures from "../screens/settings/SettingsClosures";
import SettingsImport from "../screens/settings/SettingsImport";
import { StaffLibrary, StaffGroups } from "../screens/staff/StaffList";
import GuestsList from "../screens/guests/GuestsList";
import GuestProfile from "../screens/guests/GuestProfile";
import SettingsGeneral from "../screens/settings/SettingsGeneral";
import AccountDetails from "../screens/account/AccountDetails";
import SettingsApprovals, { ApprovalProcessEditor } from "../screens/settings/SettingsApprovals";
import SettingsAI from "../screens/settings/SettingsAI";
import EventDay from "../screens/eventday/EventDay";
import SettingsIntegrations from "../screens/settings/SettingsIntegrations";
import SettingsCustomerTypes from "../screens/settings/SettingsCustomerTypes";
import IntegrationDetail from "../screens/settings/IntegrationDetail";
import SettingsCalendar from "../screens/settings/SettingsCalendar";
import SettingsManageLinks from "../screens/settings/SettingsManageLinks";
import SettingsSecurity from "../screens/settings/SettingsSecurity";
import SettingsAuditLog from "../screens/settings/SettingsAuditLog";
import ApiObservability from "../screens/settings/ApiObservability";
import StaffEditorPage from "../screens/staff/StaffEditorPage";
import { ResourcesLibrary, ResourceGroups } from "../screens/resources/ResourcesList";
import ResourceEditorPage from "../screens/resources/ResourceEditorPage";
import ServicesLibrary, { ServiceCategories, ServicePackages } from "../screens/services/ServicesScreens";
import ProductsLibrary from "../screens/products/ProductsScreens";
import ProductEditorPage from "../screens/products/ProductEditorPage";
import CategoryEditorPage, { PackageEditorPage } from "../screens/services/ServiceExtras";
import ServiceEditorPage from "../screens/services/ServiceEditorPage";
import { ReportsByGuest, ReportsRevenue, ReportsByCampaign, ReportsByEvent } from "../screens/reports/ReportsExtra";
import WorkforceSchedule from "../screens/workforce/WorkforceSchedule";
import WorkforceForecast from "../screens/workforce/WorkforceForecast";
import WorkforceCoverage from "../screens/workforce/WorkforceCoverage";
import WorkforceStaffing, { EventStaffingDetail } from "../screens/workforce/WorkforceStaffing";
import WorkforceOps from "../screens/workforce/WorkforceOps";
import WorkforceRequests from "../screens/workforce/WorkforceRequests";
import WorkforceRules from "../screens/workforce/WorkforceRules";
import WorkforceMyDay from "../screens/workforce/WorkforceMyDay";
// Working copy of My Day (2026-07-30, Marcus). Additive: the live
// `workforce.myday` route is unchanged. See screens/workforce/sandbox/.
import WorkforceMyDaySandbox from "../screens/workforce/sandbox/WorkforceMyDaySandbox";
// Full day/week/month schedule behind the working copy (2026-07-31, Marcus).
import WorkforceMyScheduleSandbox from "../screens/workforce/sandbox/WorkforceMyScheduleSandbox";
// Merged "My Schedule & Time" working copy (2026-07-31, Marcus) — one rail item
// combining the schedule surface and everything My Time carried.
import WorkforceScheduleTimeSandbox from "../screens/workforce/sandbox/WorkforceScheduleTimeSandbox";
// My Skills & Services (2026-08-01, Marcus) — the services he can deliver.
import WorkforceMySkills from "../screens/workforce/WorkforceMySkills";
import WorkforceMyPerformance from "../screens/workforce/WorkforceMyPerformance";
import WorkforceRelationships from "../screens/workforce/WorkforceRelationships";
/* Unmounted 2026-08-03 (see "ops.services" below): superseded by WorkforceServiceCatalogue.
 * Import removed with it; the file remains in the tree awaiting a deletion-impact review. */
/* REQ-128 (2026-08-03): the family-tree catalogue. ONE screen, three audiences — Riley
 * configures (`mode="config"`), the branch manager and the advisor read the same rows with
 * no controls. The six-row pre-family-tree catalogue it replaced was deleted 2026-08-26. */
import WorkforceServiceCatalogue from "../screens/workforce/WorkforceServiceCatalogue";
import WorkforceBranchCoverage from "../screens/workforce/WorkforceBranchCoverage";
import WorkforceRequisitions from "../screens/workforce/WorkforceRequisitions";
import WorkforceHRPlanning from "../screens/workforce/WorkforceHRPlanning";
import WorkforceCoaching from "../screens/workforce/WorkforceCoaching";
import WorkforceAssist from "../screens/workforce/WorkforceAssist";
import WorkforceAdvisors from "../screens/workforce/WorkforceAdvisors";
import WorkforceDraftReview from "../screens/workforce/WorkforceDraftReview";
import WorkforceOpsToday from "../screens/workforce/WorkforceOpsToday";
import WorkforceOpsWalkIns from "../screens/workforce/WorkforceOpsWalkIns";
import StoreFloor, { Clienteling } from "../screens/storeops/StoreFloor";
import { RegionOverview, makeStoreOpsPending } from "../screens/storeops/StoreOpsMore";
// Aperture Bank PORT-DUE wave (P2 close-out, 2026-07-20) — every remaining
// bank surface is a real ported screen on kit-served ops; BankPending retired.
import WorkforceSetup from "../screens/workforce/WorkforceSetup";
import WorkforceCopilot from "../screens/workforce/WorkforceCopilot";
import WorkforceRuns from "../screens/workforce/WorkforceRuns";
import WorkforceMarketplace from "../screens/workforce/WorkforceMarketplace";
import WorkforceReporting from "../screens/workforce/WorkforceReporting";
import WorkforcePayroll from "../screens/workforce/WorkforcePayroll";
import WorkforceCompliance from "../screens/workforce/WorkforceCompliance";
import WorkforceIntegration from "../screens/workforce/WorkforceIntegration";
import WorkforceRegion from "../screens/workforce/WorkforceRegion";
import WorkforceRegionCompare from "../screens/workforce/WorkforceRegionCompare";
import WorkforceRedistribute from "../screens/workforce/WorkforceRedistribute";
import WorkforceRegionRuns from "../screens/workforce/WorkforceRegionRuns";
import WorkforceRegionScope from "../screens/workforce/WorkforceRegionScope";
import WorkforceFloor from "../screens/workforce/WorkforceFloor";
/* Calendar insertion phase 2 (2026-08-08, Greg): the Floor Calendar day board —
   served data only (docs/floor-calendar-port.md). */
import FloorCalendar from "../screens/floor/FloorCalendar";
/* Calendar insertion, region layer (2026-08-10): the Regional Calendar — served
   roster + derived health (docs/floor-calendar-port.md). */
import RegionalCalendar from "../screens/floor/RegionalCalendar";
/* Unified administrator, Phase 1 (2026-08-05): the section index behind the
   single "Workforce" rail entry. Lists all 27 workforce surfaces, each row
   capability-gated, so the same screen is correct for a narrow persona. */
import WorkforceSection from "../screens/workforce/WorkforceSection";
/* Events section index (2026-08-05): Tickets / Booking questions / Surveys /
   Coupons / Landing pages live behind one Events rail entry. Same pattern as
   WorkforceSection — one way of expressing an oversized section, not two. */
import QueuesHome from "../screens/queues/QueuesHome";
import JourneysHome from "../screens/journeys/JourneysHome";
import NewJourneys from "../screens/journeys/NewJourneys";
import ConfirmationPages from "../screens/journeys/ConfirmationPages";
import AllJourneys from "../screens/journeys/AllJourneys";
import CustomersList from "../screens/customers/CustomersList";
import CustomerDetail from "../screens/customers/CustomerDetail";
import QueueConsole from "../screens/queues/QueueConsole";
import QueueHealth from "../screens/queues/QueueHealth";
import QueueDashboard from "../screens/queues/QueueDashboard";
import QueueMessageBoards from "../screens/queues/QueueMessageBoards";
import QueueArchive from "../screens/queues/QueueArchive";
import QueueDisplay from "../screens/queues/QueueDisplay";
import QueueJoin from "../screens/queues/QueueJoin";
// Payments admin (2026-08-04) — Orders ledger + Billing/Tax settings tabs.
import OrdersScreen from "../screens/orders/OrdersScreen";
import SettingsBilling from "../screens/settings/SettingsBilling";
import SettingsTax from "../screens/settings/SettingsTax";
import { useT, t as i18nT } from "../lib/i18n";

/**
 * Screen registry — route id → component, each rendered inside the real shell
 * chrome (<Screen> = Topbar + left IconRail). As a screen is ported it's added
 * to REAL_SCREENS and replaces its stub. Stubs are an honest "not yet ported"
 * interim (routed, inside real chrome) — NOT demo content.
 */

/** Map a route id → the rail item that should show active. */
function railForRoute(routeId: string): string | false {
  if (routeId === "signin") return false;
  if (routeId === "dashboard") return "dashboard";
  const seg = routeId.split(".")[0];
  const MAP: Record<string, string> = {
    events: "events", templates: "events", "ticket-groups": "tickets", "ticket-types": "tickets",
    create: "events", calendar: "calendar", questions: "cs-questions", services: "cs-services",
    eventday: "eventday",
    queues: "q-board", appointments: "appt-book", "appointment.manage": "appt-book", "appointment.detail": "appt-book", promos: "promos", messaging: "messaging",
    products: "products",
    campaigns: "campaigns", guests: "guests", segments: "guests", waitlist: "guests", invite: "guests",
    locations: "locations", nodes: "nodes", "nodes.attributes": "nodes", people: "people", "people.person": "people", staff: "staff", resources: "resources", approvals: "approvals",
    reports: "reports", branding: "branding", landing: "branding", hub: "branding",
    settings: "settings", account: "settings", notifications: "dashboard", inbox: "messaging",
    checkins: "events", registrations: "events", vip: "events", event: "events",
    workforce: "wf-schedule",
    storeops: "so-floor",
    customers: "customers", "customers.detail": "customers",
    orders: "orders",
    /* Manage Pages owns its own rail entry as of 2026-09-09 — it is a module,
       not a tab on the designer beside it. */
    confirmpages: "manage-pages",
    alljourneys: "all-journeys",
  };
  return MAP[seg] ?? "events";
}

/**
 * Unmigrated-route placeholder — NATIVE, tenant-agnostic, no iframe.
 *
 * The deployable app NEVER embeds the legacy prototype (it's a B-vestige that
 * can't satisfy the platform's tenant-agnostic CI + SPA hosting — see
 * CLAUDE.md "Unmigrated screens"). A not-yet-migrated route renders this clean
 * "being migrated" state inside the real A shell + Unmigrated badge.
 *
 * CD-PREVIEW ONLY: when CD's local esbuild-wasm harness sets
 * `window.__CD_LEGACY_PREVIEW__ = true`, we ALSO embed the legacy design via
 * iframe so CD can eyeball the previous design while porting. That flag is
 * NEVER set in a production build, so the deployed app ships the native
 * placeholder alone — no iframe, no recursion, no legacy source required.
 */
const LEGACY_PREVIEW = typeof window !== "undefined" && (window as { __CD_LEGACY_PREVIEW__?: boolean }).__CD_LEGACY_PREVIEW__ === true;

function MigrationPlaceholder({ routeId }: { routeId: string }) {
  const t = useT();
  if (LEGACY_PREVIEW) {
    const onLoad = (e: React.SyntheticEvent<HTMLIFrameElement>) => {
      try {
        const doc = e.currentTarget.contentDocument;
        if (!doc) return;
        const style = doc.createElement("style");
        style.textContent = 'header[role="banner"], nav[aria-label="Primary navigation"], .a11y-skip { display: none !important; }';
        doc.head.appendChild(style);
      } catch { /* noop */ }
    };
    return (
      <iframe
        title={i18nT("shell.screens.routeidPreviousDesignCdPreview.icu", { value0: routeId })}
        src={`./legacy-prototype-source/index.html#${routeId}`}
        onLoad={onLoad}
        style={{ flex: 1, minHeight: 0, width: "100%", border: "none", background: "var(--jrni-color-surface-canvas)" }}
      />
    );
  }
  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
      <div style={{ maxWidth: 380, textAlign: "center" }}>
        <div style={{ width: 52, height: 52, borderRadius: 12, background: "var(--jrni-color-primary-4)", color: "var(--jrni-color-primary-1)", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
          <Icon.refresh size={22} />
        </div>
        <div style={{ fontSize: 11, color: "var(--jrni-color-text-soft)", textTransform: "uppercase", letterSpacing: 0.06, fontWeight: 700, marginBottom: 6 }}>{t("shell.screens.beingMigrated", "Being migrated")}</div>
        <h2 style={{ fontSize: 19, fontWeight: 700, color: "var(--jrni-color-neutral-1)", margin: "0 0 8px" }}>{t("shell.screens.thisScreenIsComingOverNext", "This screen is coming over next")}</h2>
        <p style={{ fontSize: 13.5, color: "var(--jrni-color-neutral-2)", lineHeight: 1.55, margin: 0 }}>
          {t("shell.screens.weReMoving", "We're moving")}{" "}<code style={{ fontFamily: "var(--jrni-font-family-mono)", fontSize: 12, background: "var(--jrni-color-neutral-6)", padding: "1px 5px", borderRadius: 4 }}>{routeId}</code> {t("shell.screens.ontoTheNewPlatformItLlAppearHereWiredToLiveData", "onto the new platform. It'll appear here, wired to live data, as soon as it's migrated.")}</p>
      </div>
    </div>
  );
}

function makeStub(routeId: string): ComponentType {
  return function ScreenStub() {
    return (
      <Screen rail={railForRoute(routeId)}>
        <MigrationPlaceholder routeId={routeId} />
      </Screen>
    );
  };
}

/** B1 (2026-07-21 pushback docket): an already-authenticated user landing on the
 * `signin` route (hard-refresh of /signin) must NOT fall through to the migration
 * placeholder — sign-in is an auth-gate concern handled in App.tsx before this
 * router mounts. Redirect to the default authed route instead. */
function SigninRedirect(): null {
  const nav = useNav();
  useEffect(() => { nav.replace("dashboard"); }, [nav]);
  return null;
}

/** Real ported screens override stubs here as they land. */
const REAL_SCREENS: Partial<Record<RouteId, ComponentType>> = {
  signin: SigninRedirect,
  // Appointments process (2026-07-26, APT-13) — the day book replaces the
  // migration stub; appointment.manage is the public self-service page.
  appointments: AppointmentsBook,
  // The record as a dedicated page (2026-08-06, operator: the side drawer was
  // too small for the full booking). Declares rail="appt-book" itself.
  "appointment.detail": AppointmentDetail,
  "appointment.manage": ManageBooking,
  events: EventsList,
  dashboard: Dashboard,
  /* The event creation wizard was decommissioned 2026-08-24 (operator). Its
     thirteen step screens and the events section index are deleted; the Journey
     designer ("newjourneys") owns event create and edit. */
  locations: LocationsList,
  "settings.locations": LocationsList,
  "locations.edit": LocationEditorPage,
  "locations.import": LocationsImport,
  nodes: NodeTreeScreen,
  "nodes.attributes": AttributesScreen,
  templates: EventTemplates,
  "templates.detail": TemplateDetail,
  promos: PromosLibrary,
  "promos.edit": PromoEditorPage,
  "ticket-types": TicketTypesLibrary,
  "ticket-types.edit": TicketTypeEditorPage,
  "ticket-groups": TicketGroupsLibrary,
  "ticket-groups.edit": TicketGroupEditorPage,
  "events.archived": EventsArchived,
  "landing.pages": LandingPagesIndex,
  "hub.pages": HubPagesIndex,
  "hub.editor": HubEditor,
  "hub.create.basics": HubCreateBasics,
  "hub.create.page": HubCreatePage,
  "hub.create.publish": HubCreatePublish,
  "hub.create.live": HubCreateLive,
  "landing.editor": LandingPageEditor,
  "hub.public.workspace": PublicWorkspaceHub,
  "hub.public.location": PublicLocationHub,
  "hub.public.group": PublicGroupHub,
  "event.public": PublicEventLanding,
  "event.register": PublicEventRegister,
  /* People (2026-08-06) — one roster for users AND bookable staff, the
     administrator's add/edit surface in every vertical. Settings › Team & roles
     was merged into this screen and retired (2026-08-10); `staff` still exists. */
  /* Split 2026-09-10 (operator): `people` is the Staff roster (People tab) and
     `roles` is role definition, System-Administrator-only. Groups is retired
     as a tab; `staff.groups` still owns group management. */
  people: StaffAndRoles,
  staff: StaffLibrary,
  "staff.groups": StaffGroups,
  "staff.edit": StaffEditorPage,
  "staff.group.edit": StaffGroups,
  resources: ResourcesLibrary,
  "resources.groups": ResourceGroups,
  "resources.edit": ResourceEditorPage,
  "resources.group.edit": ResourceGroups,
  guests: GuestsList,
  "guests.profile": GuestProfile,
  "settings.general": SettingsGeneral,
  "account.details": AccountDetails,
  eventday: EventDay,
  "settings.approvals": SettingsApprovals,
  "settings.ai": SettingsAI,
  "settings.integ": SettingsIntegrations,
  "settings.customerTypes": SettingsCustomerTypes,
  "settings.integ.detail": IntegrationDetail,
  "settings.calendar": SettingsCalendar,
  "settings.manageLinks": SettingsManageLinks,
  "settings.security": SettingsSecurity,
  "settings.audit": SettingsAuditLog,
  "settings.api": ApiObservability,
  "settings.branding": SettingsBranding,
  branding: SettingsBranding,
  "settings.closures": SettingsClosures,
  "settings.import": SettingsImport,
  // Payments admin (2026-08-04) — Orders ledger + Billing/Tax settings tabs.
  orders: OrdersScreen,
  "settings.billing": SettingsBilling,
  "settings.tax": SettingsTax,
  inbox: InboxView,
  campaigns: CampaignsList,
  "campaigns.sent": CampaignsList,
  "campaigns.scheduled": CampaignsList,
  "campaigns.drafts": CampaignsList,
  "campaigns.approvals": CampaignsList,
  approvals: ApprovalsInbox,
  "approvals.review": ApproverReviewScreen,
  "approvals.process.edit": ApprovalProcessEditor,
  /* Campus Pulse is the DEFAULT report (2026-08-31, operator): `reports` renders
     it, and the events fill-rate console keeps its own route so the tab strip and
     the rail highlight both still resolve (railHit rule 2 matches `reports.*`).
     `reports.pulse` stays as an alias for saved deep links. */
  reports: ReportsCampusPulse,
  "reports.events": ReportsOverview,
  "reports.pulse": ReportsCampusPulse,
  "reports.guests": ReportsByGuest,
  "reports.campaigns": ReportsByCampaign,
  "reports.revenue": ReportsRevenue,
  // #805 — a REAL By-event screen; previously mapped to ReportsOverview with a
  // hardcoded activeTab, so the tab changed the URL but not the content.
  "reports.event": ReportsByEvent,
  "reports.appointments": ReportsAppointments,
  "reports.library": EventReportsLibrary,
  "reports.detail": EventReportDetail,
  "calendar.week": CalendarWeek,
  "calendar.month": CalendarMonth,
  "calendar.day": CalendarDay, // #806 — Calendar migration complete (day view)
  notifications: NeedsAttention, // #804A — Needs Attention screen (topbar bell)
  questions: QuestionsLibrary,
  "questions.groups": QuestionGroupsLibrary,
  "questions.edit": QuestionEditorPage,
  "questions.group.edit": QuestionGroupEditorPage,
  "messaging.flow": FlowBuilderPage,
  messaging: MessagingLibrary,
  "messaging.compose": MessageComposer,
  // Full-screen email design canvas — declares rail="messaging" like the composer,
  // so Messaging stays the single highlighted rail item (rail HARD RULE).
  "messaging.compose.email": EmailDesigner,
  "messaging.flows": FlowsLibrary,
  "messaging.delivery": DeliveryLog,
  "messaging.appointments": ApptComms,
  "messaging.automatic": AutomaticEmails,
  "messaging.settings": MessagingSettingsScreen,
  surveys: SurveysPage,
  "surveys.questions": SurveyQuestionsPage,
  "surveys.question.edit": QuestionEditorPage,
  "surveys.edit": SurveyEditorPage,
  services: ServicesLibrary,
  "services.categories": ServiceCategories,
  "services.packages": ServicePackages,
  "services.category.edit": CategoryEditorPage,
  "services.package.edit": PackageEditorPage,
  "services.edit": ServiceEditorPage,
  products: ProductsLibrary,
  "products.edit": ProductEditorPage,
  "workforce.schedule": WorkforceSchedule,
  "workforce.forecast": WorkforceForecast,
  "workforce.coverage": WorkforceCoverage,
  "workforce.staffing": WorkforceStaffing,
  "workforce.staffing.event": EventStaffingDetail,
  "workforce.ops": WorkforceOps,
  "workforce.requests": WorkforceRequests,
  "workforce.rules": WorkforceRules,
  // `workforce.my` (MySchedule) retired 2026-07-29 — no persona in any vertical
  // reached it; My Time carries the same surfaces as tabs. quarantine/2026-0729-myschedule-screen/
  "workforce.myday": WorkforceMyDay,
  // Working copy — opens from the "My Day (copy)" rail item in the Personal
  // Banker profile. Declares rail="wf-myday-copy", so that single item lights up
  // and My Day stays dark (rail HARD RULE).
  "workforce.myday.sandbox": WorkforceMyDaySandbox,
  // "Manage my time → Manage my schedule" from the copy. Declares
  // rail="wf-myday-copy" too, so My Day (copy) stays the single lit item and
  // My Day stays dark (rail HARD RULE).
  "workforce.myschedule.sandbox": WorkforceMyScheduleSandbox,
  // The rail's "My Schedule & Time" (2026-07-31). Declares rail="wf-mysched";
  // the standalone schedule route above declares the same id, so exactly one
  // item lights on either route (rail HARD RULE).
  "workforce.scheduletime.sandbox": WorkforceScheduleTimeSandbox,
  // My Skills & Services (2026-08-01) — declares rail="wf-myskills", its own item.
  "workforce.myskills": WorkforceMySkills,
  // Queues process (section tabs: Queues · Display Boards · Archive; Queue
  // health opens from a queue's ⋯ menu). Queue Journeys and the queue wizard
  // were removed 2026-08-26 — the Journey Designer (`newjourneys`) owns both
  // creating and editing a queue's journey.
  queues: QueuesHome,
  journeys: JourneysHome,
  // New Journeys (2026-08-17) — the unified Journey designer, ported 1:1 from the
  // Gold Master prototype; deliberately prototype-styled (operator ruling).
  newjourneys: NewJourneys,
  // Confirmation pages (2026-08-31) — post-booking page library + designer, ported
  // 1:1 from the Gold Master prototype; prototype-styled like the designer it sits
  // beside. Documents persist through confirmation-pages-api (local stub until the
  // contract lands — see CD-API-REQUEST-confirmation-pages-2026-08-31.md).
  confirmpages: ConfirmationPages,
  // All Journeys (2026-09-03) — the Appointment Page Designer, ported 1:1 from the
  // Gold Master prototype `Appointment Page Designer.dc.html`. Records persist through
  // alljourneys-api (one record per journey per tenant, local stub until the contract lands).
  alljourneys: AllJourneys,
  customers: CustomersList,
  "customers.detail": CustomerDetail,
  "queues.console": QueueConsole,
  "queues.health": QueueHealth,
  "queues.dashboard": QueueDashboard,
  "queues.boards": QueueMessageBoards,
  "queues.archive": QueueArchive,
  "queue.join": QueueJoin,
  "queue.display": QueueDisplay,
  "storeops.floor": StoreFloor,
  "storeops.clients": Clienteling,
  "storeops.region": RegionOverview,
  "storeops.insights": makeStoreOpsPending("storeops.insights"),
  "storeops.events": makeStoreOpsPending("storeops.events"),
  "storeops.roster": makeStoreOpsPending("storeops.roster"),
  "storeops.skills": makeStoreOpsPending("storeops.skills"),
  "storeops.perf": makeStoreOpsPending("storeops.perf"),
  "storeops.loyalty": makeStoreOpsPending("storeops.loyalty"),
  "storeops.targets": makeStoreOpsPending("storeops.targets"),
  "storeops.schedules": makeStoreOpsPending("storeops.schedules"),
  "storeops.staff": makeStoreOpsPending("storeops.staff"),
  "storeops.integrations": makeStoreOpsPending("storeops.integrations"),
  "storeops.compliance": makeStoreOpsPending("storeops.compliance"),
  "storeops.settings": makeStoreOpsPending("storeops.settings"),
  "storeops.my.day": makeStoreOpsPending("storeops.my.day"),
  "storeops.my.book": makeStoreOpsPending("storeops.my.book"),
  "storeops.my.targets": makeStoreOpsPending("storeops.my.targets"),
  "storeops.my.schedule": makeStoreOpsPending("storeops.my.schedule"),
  // Aperture Bank six-persona port — P2 CLOSED OUT 2026-07-20: every rail
  // entry is a real ported screen on kit-served ops (PORT-DUE wave).
  "workforce.setup": WorkforceSetup,
  "workforce.copilot": WorkforceCopilot,
  "workforce.runs": WorkforceRuns,
  "workforce.draft": WorkforceDraftReview,
  "workforce.marketplace": WorkforceMarketplace,
  "workforce.reporting": WorkforceReporting,
  "workforce.payroll": WorkforcePayroll,
  "workforce.advisors": WorkforceAdvisors,
  "workforce.compliance": WorkforceCompliance,
  "workforce.integration": WorkforceIntegration,
  "workforce.planning": WorkforceHRPlanning,
  "workforce.coaching": WorkforceCoaching,
  "workforce.assists": WorkforceAssist,
  "workforce.requisitions": WorkforceRequisitions,
  "workforce.region": WorkforceRegion,
  "workforce.region.compare": WorkforceRegionCompare,
  "workforce.region.redistribute": WorkforceRedistribute,
  "workforce.region.runs": WorkforceRegionRuns,
  "workforce.region.scope": WorkforceRegionScope,
  "workforce.floor": WorkforceFloor,
  "floor.calendar": FloorCalendar,
  "floor.region": RegionalCalendar,
  /* Phase 1 (2026-08-05): the ONE Workforce rail entry lands here. Route id is
     the bare section name so the rail's longest-match highlight rule keeps every
     `workforce.*` child lighting this single item. */
  workforce: WorkforceSection,
  "workforce.myperf": WorkforceMyPerformance,
  "workforce.myrelationships": WorkforceRelationships,
  // `workforce.mytime` RETIRED 2026-08-01 (operator): My Time IS "My Schedule &
  // Time" now — the merged screen owns time off, open shifts, swaps, preferences
  // and history as tabs. WorkforceMyTime.tsx stays as the module that OWNS those
  // tab + modal components (the merged screen imports all seven); only the screen
  // and its route are gone. Rail id "wf-mytime" is now an alias to the merged
  // route. quarantine/2026-0801-mytime-screen-retired/
  "ops.today": WorkforceOpsToday,
  /* Riley's Services screen IS the catalogue (operator, 2026-08-03: "I would have expected you
     to place what is in Service Catalogue on the Services screen for Riley"). The six-row
     pre-family-tree version it replaced was deleted 2026-08-26. `ops.catalogue` remains
     as an ALIAS so any saved link still resolves to the same screen. */
  "ops.services": () => <WorkforceServiceCatalogue mode="config" rail="appt-services" />,
  "ops.catalogue": () => <WorkforceServiceCatalogue mode="config" rail="appt-services" />,
  "workforce.services.view": () => <WorkforceServiceCatalogue mode="view" rail="wf-services-view" />,
  /* The branch manager's staff-coverage screen (2026-08-03): who can deliver what, and where the
     branch has nobody signed off. She configures on her team's behalf; it lands on their own
     Services screen through the same override the catalogue reads. */
  "workforce.coverage.staff": WorkforceBranchCoverage,
  "ops.walkins": WorkforceOpsWalkIns,
};

export const SCREENS: Record<string, ComponentType> = Object.fromEntries(
  ROUTE_IDS.map((id) => [id, REAL_SCREENS[id] ?? makeStub(id)]),
);

/**
 * Routes fully wired to the data layer. Single source of truth for migration
 * state — anything NOT here renders the "Unmigrated" badge (see AppRouter).
 * A route graduates the moment it's added to REAL_SCREENS above.
 */
export const MIGRATED_ROUTES = new Set<string>(Object.keys(REAL_SCREENS));
